import React from 'react';

/** Gap between the navigation items — keep in sync with `.psf-header-nav__container`. */
const ITEM_GAP = 10;

export interface NavigationOverflow {
  /** The row the items are laid out in; its width decides how many fit. */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Hidden row holding every item, used to read the individual widths. */
  measureRef: React.RefObject<HTMLDivElement | null>;
  /** The burger button, which always stays and therefore takes room away. */
  burgerRef: React.RefObject<HTMLDivElement | null>;
  /** How many of the items fit into the row; the rest belong in the burger menu. */
  visibleCount: number;
}

/**
 * Works out how many navigation items fit on one line. Instead of dropping the
 * whole navigation at a fixed screen width, the caller renders the first
 * `visibleCount` items and moves the remainder into the burger menu, so a wide
 * window shows everything and a narrow one still shows as much as it can.
 *
 * The widths are read from a copy of the row that is rendered but hidden, which
 * keeps measuring independent of what is currently on screen.
 */
export const useNavigationOverflow = (itemCount: number, itemsKey: string): NavigationOverflow => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const measureRef = React.useRef<HTMLDivElement>(null);
  const burgerRef = React.useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = React.useState(itemCount);

  React.useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) {
      return;
    }

    const recalculate = () => {
      const style = window.getComputedStyle(container);
      const burgerWidth = burgerRef.current?.offsetWidth ?? 0;
      const available =
        container.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight) - burgerWidth - ITEM_GAP;

      let used = 0;
      let fitting = 0;
      for (const child of Array.from(measure.children)) {
        const width = child.getBoundingClientRect().width + (fitting > 0 ? ITEM_GAP : 0);
        if (used + width > available) {
          break;
        }
        used += width;
        fitting += 1;
      }

      setVisibleCount(fitting);
    };

    recalculate();

    // The container reacts to the window, the hidden row to anything that
    // changes the item widths itself — a late-loading font, most of all.
    const observer = new ResizeObserver(recalculate);
    observer.observe(container);
    observer.observe(measure);
    return () => observer.disconnect();
  }, [itemsKey]);

  return { containerRef, measureRef, burgerRef, visibleCount };
};
